import React, { useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { createSelector } from '@reduxjs/toolkit';

import type { ISortedAvailableShift } from '@/types/schedule';

import { Button } from '@/components/common';
import { PickedShiftsScheduleItem, ModalClearShifts } from '@/components/agent/my-schedule';
import { flex, media } from '@/styles/theme';
import { assignAvailableShifts } from '@/store/slices/scheduleSlice';
import { selectIsPendingAction } from '@/store/selectors/scheduleSelectors';
import { useAppSelector } from '@/hooks';
import { NAVBAR_HEIGHT, SHIFTPICKUP_CART_HEADER_HEIGHT, SHIFTPICKUP_CART_TOP_SPACE } from '@/config/variables';

const pickedShiftsSelector = createSelector(selectIsPendingAction(assignAvailableShifts.typePrefix), isPending => ({
  isPending,
}));

interface IProps {
  groupedSelectedShiftsBySchedule: { [key: string]: { [key: string]: ISortedAvailableShift[] } };
  handleClearSelectedShifts: () => void;
  handleSubmitShifts: () => Promise<void> | void;
  handleRemoveShift: (shift: ISortedAvailableShift) => void;
}

const PickedShifts = ({
  groupedSelectedShiftsBySchedule,
  handleSubmitShifts,
  handleClearSelectedShifts,
  handleRemoveShift,
}: IProps): React.ReactElement => {
  const { t } = useTranslation();
  const [showClearSelectedShiftsModal, setShowClearSelectedShiftsModal] = useState<boolean>(false);
  const { isPending } = useAppSelector(pickedShiftsSelector);

  return (
    <>
      <ContainerBody>
        {Object.values(groupedSelectedShiftsBySchedule).map((shiftsByDay, index) => (
          <PickedShiftsScheduleItem
            key={index}
            index={index}
            shiftsByDay={shiftsByDay}
            onRemoveShift={handleRemoveShift}
          />
        ))}
      </ContainerBody>
      <ContainerFooter>
        <Footer>
          <Button
            className="w-100 mx-1"
            onClick={() => setShowClearSelectedShiftsModal(true)}
            disabled={Object.keys(groupedSelectedShiftsBySchedule).length === 0 || isPending}
          >
            {t`mySchedule.pickUpShiftCart.buttonClear`}
          </Button>
          <Button
            className="w-100 mx-1"
            color="primary"
            disabled={Object.keys(groupedSelectedShiftsBySchedule).length === 0 || isPending}
            onClick={handleSubmitShifts}
          >
            {t`mySchedule.pickUpShiftCart.buttonPickShifts`}
          </Button>
        </Footer>
      </ContainerFooter>
      <ModalClearShifts
        showModal={showClearSelectedShiftsModal}
        hideModal={() => setShowClearSelectedShiftsModal(false)}
        handleClear={handleClearSelectedShifts}
      />
    </>
  );
};

export { PickedShifts };

const ContainerBody = styled.div.attrs({
  className: 'px-2',
})`
  background-color: #f9fafb;
  border: 1px solid #f8f9fa;
  overflow: hidden auto;
  max-height: calc(
    90vh - 2 * ${SHIFTPICKUP_CART_HEADER_HEIGHT}px - ${NAVBAR_HEIGHT}px - ${SHIFTPICKUP_CART_TOP_SPACE}px
  );

  ${media.phone} {
    max-height: 400px;
  }
`;

const ContainerFooter = styled.div`
  width: 100%;
  box-shadow: 0 -2px 4px rgba(0, 0, 0, 0.075);
`;

const Footer = styled.div.attrs({
  className: `
    ${flex.rowCenter} justify-content-around
    px-2 pt-2
  `,
})`
  height: 88px;
  background-color: #fff;
`;
